import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useGetUserBillingDetailsData, useUpsertBillingDetails } from '../../services';
import { useSnackbar } from 'notistack';
import { GetCountries } from 'react-country-state-city';
import { Country as CountryType } from 'react-country-state-city/dist/esm/types';
import { LOCATION_DATA_URL } from '../../utils/constant';

interface FormValues {
  name: string;
  address: string;
  country: { id: number; name: string } | null;
  state: { id: number | null; name: string } | null;
  city: string;
  pincode: string;
}

export const useBillingDetailsForm = (onSubmitCallback?: () => Promise<void>) => {
  const FormSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required('Name is required')
      .typeError('Name must be a valid string')
      .max(25, 'Name must be at most 25 characters'),
    address: Yup.string()
      .trim()
      .required('Address is required')
      .max(200, 'Address must be at most 200 characters'),
    pincode: Yup.string()
      .trim()
      .required('ZIP code is required')
      .matches(
        /^[A-Za-z0-9\s\-]{3,12}$/,
        'Enter a valid ZIP code (3-12 characters, alphanumeric, hyphens, or spaces)'
      ),
    country: Yup.object()
      .nullable()
      .required('Country is required')
      .shape({
        id: Yup.number().required('Country ID is required'),
        name: Yup.string().required('Country name is required').trim(),
      }),
    state: Yup.object()
      .nullable()
      .shape({
        id: Yup.number().nullable(),
        name: Yup.string().nullable(),
      })
      .when('$showStateField', {
        is: true,
        then: (schema) =>
          schema.shape({
            id: Yup.number().nullable(),
            name: Yup.string().trim().required('State is required'),
          }),
      }),
    city: Yup.string()
      .trim()
      .required('City is required')
      .max(200, 'City must be at most 200 characters'),
  });

  const { data: userBillingData, isLoading: isFetchingBillingDetails } =
    useGetUserBillingDetailsData();

  const { mutate: mutateUpsertBilling, isLoading: isSubmitting } = useUpsertBillingDetails();
  const { enqueueSnackbar } = useSnackbar();
  const [showStateField, setShowStateField] = useState(false);

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(FormSchema),
    context: { showStateField },
    defaultValues: {
      name: userBillingData?.name || '',
      address: userBillingData?.address || '',
      country: {
        id: userBillingData?.countryId || 233,
        name: userBillingData?.country || 'United States',
      },
      state: {
        id: userBillingData?.stateId || null,
        name: userBillingData?.state || '',
      },
      city: userBillingData?.city || '',
      pincode: userBillingData?.pincode || '',
    },
    mode: 'onChange',
  });

  const { handleSubmit, setValue, watch, reset, formState, getValues } = formMethods;
  const country = watch('country');
  const state = watch('state');
  const city = watch('city');

  useEffect(() => {
    const loadBillingData = async () => {
      if (userBillingData) {
        let countryName = userBillingData?.country || '';
        let countryId = userBillingData?.countryId || null;

        // Check if the country name from backend matches a valid country
        if (countryName) {
          try {
            const countries = (await GetCountries(LOCATION_DATA_URL)) as CountryType[];
            const matchedCountry = countries.find((c) => c.name === countryName);
            if (!matchedCountry) {
              // If country doesn't match, set to United States
              countryName = 'United States';
              countryId = 233;
            } else {
              // Use the matched country's id if not provided
              countryId = countryId || matchedCountry.id;
            }
          } catch (error) {
            // If there's an error fetching countries, default to United States
            countryName = 'United States';
            countryId = 233;
          }
        }

        reset({
          name: userBillingData?.name || '',
          address: userBillingData?.address || '',
          country: { id: countryId, name: countryName },
          state: {
            id: userBillingData?.stateId || null,
            name: userBillingData?.state || '',
          },
          city: userBillingData?.city || '',
          pincode: userBillingData?.pincode || '',
        });
      }
    };

    loadBillingData();
  }, [userBillingData, reset]);

  const onSubmit = (data: FormValues) => {
    mutateUpsertBilling(
      {
        name: data?.name,
        address: data?.address,
        country: data?.country?.name,
        countryId: data?.country?.id,
        state: data?.state?.name,
        stateId: data?.state?.id,
        city: data?.city,
        pincode: data?.pincode,
      },
      {
        onSuccess: async () => {
          if (onSubmitCallback) {
            await onSubmitCallback();
          } else {
            enqueueSnackbar('Billing Address updated successfully', { variant: 'success' });
          }
        },
        onError: (error: any) => {
          enqueueSnackbar(error?.response?.message || error?.message || 'Something went wrong', {
            variant: 'error',
          });
        },
      }
    );
  };

  useEffect(() => {
    const setInitialValues = async () => {
      if (userBillingData && !getValues('name')) {
        let countryName = userBillingData?.country || '';
        let countryId = userBillingData?.countryId;

        // Check if the country name from backend matches a valid country
        if (countryName) {
          try {
            const countries = (await GetCountries(LOCATION_DATA_URL)) as CountryType[];
            const matchedCountry = countries.find((c) => c.name === countryName);
            if (!matchedCountry) {
              // If country doesn't match, set to United States
              countryName = 'United States';
              countryId = 233;
            } else {
              // Use the matched country's id if not provided
              countryId = countryId || matchedCountry.id;
            }
          } catch (error) {
            // If there's an error fetching countries, default to United States
            countryName = 'United States';
            countryId = 233;
          }
        }

        setValue('name', userBillingData?.name);
        setValue('address', userBillingData?.address);
        setValue('country', { id: countryId, name: countryName });
        setValue('state', {
          id: userBillingData?.stateId,
          name: userBillingData?.state,
        });
        setValue('city', userBillingData?.city);
        setValue('pincode', userBillingData?.pincode);
      }
    };

    setInitialValues();
  }, [isFetchingBillingDetails, userBillingData, setValue, getValues]);

  return {
    formMethods,
    handleSubmit: handleSubmit(onSubmit),
    setValue,
    watch,
    formState,
    country,
    state,
    city,
    showStateField,
    setShowStateField,
    userBillingData,
    isFetchingBillingDetails,
    isSubmitting,
  };
};
